import apiClient from "../services/apiClient";

/**
 * Yönetim → Kullanıcılar uçları.
 *
 * Keycloak kimliği, Wiser DB yetkiyi tutar: ekran kullanıcıya ROL atar, izin matrisi
 * (rol → izin) seed'de sabittir ve buradan düzenlenmez. Kalıcı silme ucu YOKTUR —
 * "silme" = pasifleştirme.
 */

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export interface UserAdminRole {
  id: string;
  name: string;
}

export type KeycloakStatus = "enabled" | "disabled" | "missing" | "unknown";

export interface UserAdminListItem {
  memberId: string;
  userName: string;
  nameSurname: string;
  email: string | null;
  companyId: string | null;
  companyName: string | null;
  roles: UserAdminRole[];
  isActive: boolean;
  keycloakStatus: KeycloakStatus;
}

export interface UserAdminDetail extends UserAdminListItem {
  name: string | null;
  surname: string | null;
  effectivePermissions: string[];
}

export interface UserAdminCreateRequest {
  name: string;
  surname: string;
  email?: string;
  userName?: string;
  companyId: string;
  roleIds: string[];
}

export interface UserAdminUpdateRequest {
  name?: string;
  surname?: string;
  email?: string;
  companyId?: string;
}

/** Parola yalnızca bu yanıtta döner; ikinci kez okunamaz. */
export interface UserAdminCreateResult {
  memberId: string;
  userName: string;
  password: string;
  keycloakAlreadyExisted: boolean;
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === "object" && "data" in (payload as object)) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

export interface UserListFilters {
  companyId?: string;
  roleId?: string;
  isActive?: boolean;
  search?: string;
}

export const getUserList = async (
  filters: UserListFilters = {},
): Promise<UserAdminListItem[]> => {
  const params: Record<string, string | boolean> = {};
  if (filters.companyId) params.companyId = filters.companyId;
  if (filters.roleId) params.roleId = filters.roleId;
  if (typeof filters.isActive === "boolean") params.isActive = filters.isActive;
  if (filters.search) params.search = filters.search;

  const res = await apiClient.get<ApiResponse<UserAdminListItem[]>>(
    "api/UserAdmin/List",
    { params },
  );
  return unwrap(res.data) ?? [];
};

export const getUserDetail = async (
  memberId: string,
): Promise<UserAdminDetail | null> => {
  const res = await apiClient.get<ApiResponse<UserAdminDetail>>(
    `api/UserAdmin/${memberId}`,
  );
  return unwrap(res.data) ?? null;
};

export const getAssignableRoles = async (
  companyId?: string,
): Promise<UserAdminRole[]> => {
  const res = await apiClient.get<ApiResponse<UserAdminRole[]>>(
    "api/UserAdmin/AssignableRoles",
    { params: companyId ? { companyId } : {} },
  );
  return unwrap(res.data) ?? [];
};

export const createUser = async (
  body: UserAdminCreateRequest,
): Promise<UserAdminCreateResult> => {
  const res = await apiClient.post<ApiResponse<UserAdminCreateResult>>(
    "api/UserAdmin/Create",
    body,
  );
  return unwrap(res.data);
};

export const updateUser = async (
  memberId: string,
  body: UserAdminUpdateRequest,
): Promise<void> => {
  await apiClient.put(`api/UserAdmin/${memberId}`, body);
};

export const setUserRoles = async (
  memberId: string,
  roleIds: string[],
): Promise<void> => {
  await apiClient.put(`api/UserAdmin/${memberId}/Roles`, { roleIds });
};

export const regeneratePassword = async (
  memberId: string,
): Promise<UserAdminCreateResult> => {
  const res = await apiClient.post<ApiResponse<UserAdminCreateResult>>(
    `api/UserAdmin/${memberId}/RegeneratePassword`,
  );
  return unwrap(res.data);
};

export const deactivateUser = async (memberId: string): Promise<void> => {
  await apiClient.post(`api/UserAdmin/${memberId}/Deactivate`);
};

export const activateUser = async (memberId: string): Promise<void> => {
  await apiClient.post(`api/UserAdmin/${memberId}/Activate`);
};

/**
 * Varsayılan şirketin rol–izin satırlarını diğer şirketlere aktarır. Seed yalnızca varsayılan
 * şirket için yazdığı için, legacy'den gelen şirketlerdeki kullanıcıya rol atamak bu aktarım
 * yapılmadan sessizce etkisiz kalır.
 */
export const backfillRolePermissions = async (
  companyId?: string,
): Promise<{ companies: number; addedRows: number }> => {
  const res = await apiClient.post<
    ApiResponse<{ companies: number; addedRows: number }>
  >("api/UserAdmin/BackfillRolePermissions", null, {
    params: companyId ? { companyId } : {},
  });
  return unwrap(res.data);
};

/** Kullanıcının kendi parolasını değiştirmesi — UserAdmin izni gerekmez. */
export const changeMyPassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<void> => {
  await apiClient.post("api/UserAdmin/ChangeMyPassword", {
    currentPassword,
    newPassword,
  });
};

/** Backend'in ResponseObjectDTO.message alanını toast'a taşımak için. */
export const extractApiError = (error: any, fallback: string): string =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;
